/**
 * Design System Tokens
 *
 * Centralized design tokens for consistent styling across the application.
 * Import and use these constants in styled-components instead of hardcoded values.
 *
 * @example
 * import { COLORS, SPACING, Z_INDEX } from '../../styles/tokens';
 *
 * const MyComponent = styled.div`
 *   background: ${COLORS.primary[50]};
 *   padding: ${SPACING.lg};
 *   z-index: ${Z_INDEX.modal};
 * `;
 */

// ============================================================================
// COLOR PALETTE
// ============================================================================

export const COLORS = {
  // Primary Blue
  primary: {
    50: '#eff6ff',
    100: '#dbeafe',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
  },

  // Success Green
  success: {
    50: '#f0fdf4',
    500: '#10b981',
    600: '#059669',
  },

  // Warning Amber
  warning: {
    50: '#fffbeb',
    500: '#f59e0b',
    600: '#d97706',
  },

  // Danger Red
  danger: {
    500: '#ef4444',
    600: '#dc2626',
  },

  // Neutral Gray
  gray: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
} as const;

// ============================================================================
// SPACING
// ============================================================================

export const SPACING = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '32px',
  '4xl': '40px',
} as const;

// ============================================================================
// BORDER RADIUS
// ============================================================================

export const BORDER_RADIUS = {
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '20px',
  full: '9999px',
} as const;

// ============================================================================
// SHADOWS
// ============================================================================

export const SHADOWS = {
  xs: '0 1px 4px rgba(15, 23, 42, 0.06)',
  sm: '0 2px 8px rgba(0, 0, 0, 0.1)',
  md: '0 4px 12px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 30px rgba(15, 23, 42, 0.15)',
  xl: '0 20px 60px rgba(0, 0, 0, 0.3)',
} as const;

// ============================================================================
// FONT SIZES
// ============================================================================

export const FONT_SIZES = {
  xs: '12px',
  sm: '13px',
  base: '14px',
  md: '15px',
  lg: '16px',
  xl: '18px',
  '2xl': '20px',
  '3xl': '22px',
  '4xl': '28px',
} as const;

// ============================================================================
// Z-INDEX LAYERS
// ============================================================================

export const Z_INDEX = {
  // Base layer
  base: 1,

  // Dropdowns and popovers
  dropdown: 100,
  sticky: 200,

  // Overlays and modals
  overlay: 1000,
  modal: 1100,
  modalClose: 1101,
  criticalModal: 2000,

  // Question reference modal (highest priority)
  questionModal: 3000,
  questionModalClose: 3001,

  // Mobile-specific
  fab: 900,           // Floating action button
  drawer: 2000,       // Mobile drawer
} as const;

// ============================================================================
// BREAKPOINTS
// ============================================================================

export const BREAKPOINTS = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const;

// ============================================================================
// TRANSITION DURATIONS
// ============================================================================

export const TRANSITIONS = {
  fast: '0.15s',
  normal: '0.2s',
  slow: '0.3s',
} as const;

// ============================================================================
// GRADIENTS
// ============================================================================

export const GRADIENTS = {
  primary: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  danger: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  warning: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
  neutral: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
} as const;
